import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { firestoreService } from '../services/firebase'
import { roleService } from '../services/roleService'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  Users,
  UserPlus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Shield,
  Mail,
  Phone,
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { cn } from '../utils/cn'
import CustomDropdown from '../components/CustomDropdown'

export default function UserManagementPage() {
  const { currentUser } = useAuth()
  const { currentBranch, getActualBranches, getBranchById, isViewingAllBranches } = useBranch()
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [roleFilter, setRoleFilter] = useState('all')
  const [branchFilter, setBranchFilter] = useState('all')
  const [showAddUser, setShowAddUser] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [showUserDetails, setShowUserDetails] = useState(null)

  const actualBranches = getActualBranches()

  // New user form state
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'staff',
    permissions: [],
    branchId: '',
    isActive: true
  })

  useEffect(() => {
    loadData()
  }, [currentBranch?.id])

  useEffect(() => {
    // Default new-user branch to the currently selected branch (not "All Branches")
    if (!currentBranch || isViewingAllBranches()) return
    setNewUser((prev) => (prev.branchId ? prev : { ...prev, branchId: currentBranch.id }))
  }, [currentBranch?.id, isViewingAllBranches])

  async function loadData() {
    setLoading(true)
    try {
      const [usersData, rolesData] = await Promise.all([
        firestoreService.getAll('users'),
        roleService.ensureDefaultRoles()
      ])
      
      // Ensure all users have proper permissions array
      const normalizedUsers = usersData.map(user => ({
        ...user,
        permissions: Array.isArray(user.permissions) ? user.permissions : []
      }))
      
      setUsers(normalizedUsers)
      setRoles(rolesData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load user data')
    } finally {
      setLoading(false)
    }
  }

  const branchName = (branchId) => {
    if (!branchId) return 'Unassigned'
    if (branchId === 'all-branches') return 'All branches'
    return getBranchById(branchId)?.name || branchId
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm)
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive)
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter

    const effectiveBranchFilter =
      branchFilter !== 'all'
        ? branchFilter
        : !isViewingAllBranches() && currentBranch?.id
          ? currentBranch.id
          : 'all'

    const matchesBranch =
      effectiveBranchFilter === 'all' ||
      user.branchId === effectiveBranchFilter ||
      (effectiveBranchFilter === 'unassigned' && !user.branchId)
    
    return matchesSearch && matchesStatus && matchesRole && matchesBranch
  })

  const handleAddUser = async (e) => {
    e.preventDefault()
    const branchId = newUser.branchId || (!isViewingAllBranches() ? currentBranch?.id : '')
    if (!branchId && newUser.role !== 'owner') {
      toast.error('Select a branch for this user')
      return
    }
    try {
      const userData = {
        ...newUser,
        branchId: newUser.role === 'owner' ? (branchId || null) : branchId,
        displayName: `${newUser.firstName} ${newUser.lastName}`.trim(),
        createdAt: new Date(),
        createdBy: currentUser.uid
      }
      
      await firestoreService.create('users', userData)
      toast.success('User added successfully')
      setShowAddUser(false)
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'staff',
        permissions: [],
        branchId: !isViewingAllBranches() && currentBranch?.id ? currentBranch.id : '',
        isActive: true
      })
      loadData()
    } catch (error) {
      console.error('Error adding user:', error)
      toast.error('Failed to add user')
    }
  }

  const handleUpdateUser = async (e) => {
    e.preventDefault()
    if (!editingUser?.id) return
    if (editingUser.role !== 'owner' && !editingUser.branchId) {
      toast.error('Select a branch for this user')
      return
    }
    try {
      const { id, ...rest } = editingUser
      await firestoreService.update('users', id, {
        firstName: rest.firstName || '',
        lastName: rest.lastName || '',
        displayName: `${rest.firstName || ''} ${rest.lastName || ''}`.trim() || rest.displayName || '',
        email: rest.email || '',
        phone: rest.phone || '',
        role: rest.role || 'staff',
        branchId: rest.role === 'owner' ? (rest.branchId || null) : rest.branchId,
        isActive: Boolean(rest.isActive),
        updatedAt: new Date(),
      })
      toast.success('User updated successfully')
      setEditingUser(null)
      loadData()
    } catch (error) {
      console.error('Error updating user:', error)
      toast.error('Failed to update user')
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return
    
    try {
      await firestoreService.delete('users', userId)
      toast.success('User deleted successfully')
      loadData()
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user')
    }
  }

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await firestoreService.update('users', userId, { isActive: !currentStatus })
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`)
      loadData()
    } catch (error) {
      console.error('Error updating user status:', error)
      toast.error('Failed to update user status')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!currentBranch && actualBranches.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No branches available</h3>
          <p className="text-gray-600">Create a branch first, then assign users to it.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <Users className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            User Management
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Assign each staff member to a branch. Owners can work across all branches.
          </p>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowAddUser(true)}
            className="rounded-xl bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <UserPlus className="h-5 w-5 inline mr-2 group-hover:rotate-12 transition-transform duration-300" />
            Add New User
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <CustomDropdown
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
            />
            <CustomDropdown
              options={[
                { value: 'all', label: 'All Roles' },
                ...roles.map(role => ({ value: role.key || role.id, label: role.name || role.key || role.id }))
              ]}
              value={roleFilter}
              onChange={setRoleFilter}
              placeholder="Role"
            />
            <CustomDropdown
              options={[
                { value: 'all', label: 'All Branches' },
                { value: 'unassigned', label: 'Unassigned' },
                ...actualBranches.map((b) => ({ value: b.id, label: b.name })),
              ]}
              value={branchFilter}
              onChange={setBranchFilter}
              placeholder="Branch"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50">
              <thead className="bg-muted/50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="relative px-6 py-4">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200/30">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50/50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-12 w-12 flex-shrink-0">
                          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                            <span className="text-sm font-bold text-primary">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-600">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center mr-3 text-violet-700 dark:text-violet-400">
                          <Shield className="h-4 w-4" />
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {user.role}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-700">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400 shrink-0" />
                        <span>{branchName(user.branchId)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                        user.isActive 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      )}>
                        {user.isActive ? (
                          <>
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-gray-400" />
                        {user.phone || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setShowUserDetails(user)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-xl transition-all duration-200"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-xl transition-all duration-200"
                          title="Edit User"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                          className={cn(
                            "p-2 rounded-xl transition-all duration-200",
                            user.isActive 
                              ? "text-red-600 hover:text-red-900 hover:bg-red-50" 
                              : "text-green-600 hover:text-green-900 hover:bg-green-50"
                          )}
                          title={user.isActive ? "Deactivate User" : "Activate User"}
                        >
                          {user.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                  <UserPlus className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Add New User</h3>
              </div>
              
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      required
                      value={newUser.firstName}
                      onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      required
                      value={newUser.lastName}
                      onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                  <CustomDropdown
                    options={roles.map(role => ({ value: role.key || role.id, label: role.name || role.key || role.id }))}
                    value={newUser.role}
                    onChange={(val) => setNewUser({...newUser, role: val})}
                    placeholder="Select Role"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Branch {newUser.role === 'owner' ? '(optional for owners)' : ''}
                  </label>
                  <CustomDropdown
                    options={actualBranches.map((b) => ({ value: b.id, label: b.name }))}
                    value={newUser.branchId}
                    onChange={(val) => setNewUser({ ...newUser, branchId: val })}
                    placeholder="Select branch"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Staff and managers only see data for their assigned branch.
                  </p>
                </div>
                
                <div className="flex items-center pt-4">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newUser.isActive}
                    onChange={(e) => setNewUser({...newUser, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                    Active User
                  </label>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    Add User
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddUser(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Details</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{showUserDetails.firstName} {showUserDetails.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{showUserDetails.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{showUserDetails.phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="mt-1 text-sm text-gray-900">{showUserDetails.role}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Branch</label>
                  <p className="mt-1 text-sm text-gray-900">{branchName(showUserDetails.branchId)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {showUserDetails.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Permissions</label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Array.isArray(showUserDetails.permissions) && showUserDetails.permissions.length > 0 ? (
                      showUserDetails.permissions.map(permission => (
                        <span
                          key={permission}
                          className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800"
                        >
                          {permission.replace(/_/g, ' ')}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No permissions assigned</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {showUserDetails.createdAt ? new Date(showUserDetails.createdAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowUserDetails(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                  <Edit className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Edit User</h3>
              </div>

              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                    <input
                      type="text"
                      required
                      value={editingUser.firstName || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      required
                      value={editingUser.lastName || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                  <CustomDropdown
                    options={roles.map((role) => ({ value: role.key || role.id, label: role.name || role.key || role.id }))}
                    value={editingUser.role || 'staff'}
                    onChange={(val) => setEditingUser({ ...editingUser, role: val })}
                    placeholder="Select Role"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Branch {editingUser.role === 'owner' ? '(optional for owners)' : ''}
                  </label>
                  <CustomDropdown
                    options={actualBranches.map((b) => ({ value: b.id, label: b.name }))}
                    value={editingUser.branchId || ''}
                    onChange={(val) => setEditingUser({ ...editingUser, branchId: val })}
                    placeholder="Select branch"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    This controls which branch the user works in (queue, POS, cash-ups).
                  </p>
                </div>

                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    id="editIsActive"
                    checked={Boolean(editingUser.isActive)}
                    onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="editIsActive" className="ml-2 block text-sm text-gray-700">
                    Active User
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    Save changes
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 